import { useState } from "react";
import { motion } from "framer-motion";
import {
  Linkedin,
  Instagram,
  UserPlus,
  MessageSquare,
  Sparkles,
  Send,
  Globe,
  Mail,
  Building2,
  TrendingUp,
} from "lucide-react";
import PageHeader from "@/components/shared/PageHeader";
import StatCard from "@/components/shared/StatCard";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { LINKEDIN_PROSPECTS, INSTAGRAM_PROSPECTS } from "@/data/mockData";
import { toast } from "sonner";

export default function SocialAutomation() {
  const [tab, setTab] = useState("linkedin");

  return (
    <div data-testid="social-page">
      <PageHeader
        eyebrow="Module 05 · LinkedIn & Instagram Automation"
        title="Social selling, on autopilot."
        description="Find decision-makers on LinkedIn, discover business accounts on Instagram, score, connect and follow-up — all inside one workflow."
        actions={
          <Button data-testid="social-launch-dm" className="rounded-full bg-indigo-500 hover:bg-indigo-400">
            <MessageSquare className="h-3.5 w-3.5 mr-1.5" /> Launch DM campaign
          </Button>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
        <StatCard testId="social-stat-prospects" label="New Prospects"    value="96"  delta="+18"  accent="indigo" icon={UserPlus} />
        <StatCard testId="social-stat-conns"     label="Connections Sent" value="284" delta="+42"  accent="cyan" />
        <StatCard testId="social-stat-replies"   label="Replies"          value="47"  delta="+11"  accent="pink"  icon={MessageSquare} />
        <StatCard testId="social-stat-meets"     label="Meetings"         value="12"  delta="+3"   accent="emerald" />
        <StatCard testId="social-stat-engage"    label="Engagement"       value="4.6" suffix="%"   delta="+0.3pt" accent="amber" />
        <StatCard testId="social-stat-score"     label="Avg Lead Score"   value="82"  delta="+3"   accent="cyan" icon={TrendingUp} />
      </div>

      <Tabs value={tab} onValueChange={setTab} data-testid="social-tabs">
        <TabsList className="bg-white/[0.03] border border-white/[0.06] rounded-full p-1 h-auto">
          <TabsTrigger
            data-testid="social-tab-linkedin"
            value="linkedin"
            className="rounded-full data-[state=active]:bg-indigo-500 data-[state=active]:text-white text-white/60 px-4 py-1.5"
          >
            <Linkedin className="h-3.5 w-3.5 mr-1.5" />
            LinkedIn
          </TabsTrigger>
          <TabsTrigger
            data-testid="social-tab-instagram"
            value="instagram"
            className="rounded-full data-[state=active]:bg-pink-500 data-[state=active]:text-white text-white/60 px-4 py-1.5"
          >
            <Instagram className="h-3.5 w-3.5 mr-1.5" />
            Instagram
          </TabsTrigger>
        </TabsList>

        <TabsContent value="linkedin" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {LINKEDIN_PROSPECTS.map((p, i) => (
              <LinkedinCard key={p.id} p={p} i={i} />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="instagram" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {INSTAGRAM_PROSPECTS.map((p, i) => (
              <InstagramCard key={p.id} p={p} i={i} />
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function LinkedinCard({ p, i }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: i * 0.04, duration: 0.3 }}
      className="glass-card p-5"
      data-testid={`li-card-${p.id}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-full bg-gradient-to-br from-indigo-500 to-cyan-400 grid place-items-center font-display font-semibold">
            {p.name.split(" ").map((w) => w[0]).join("")}
          </div>
          <div>
            <div className="font-display text-[15px] text-white">{p.name}</div>
            <div className="text-[12px] text-white/50">{p.title}</div>
          </div>
        </div>
        <div className="text-right">
          <div className="font-mono text-lg">{p.score}</div>
          <div className="font-mono text-[10px] uppercase tracking-wider text-white/40">score</div>
        </div>
      </div>
      <div className="mt-4 flex items-center gap-2 text-[13px] text-white/70">
        <Building2 className="h-3.5 w-3.5 text-white/40" />
        <span>{p.company}</span>
        <span className="text-white/30">·</span>
        <span className="text-white/50">{p.industry}</span>
      </div>
      <div className="mt-3 flex items-center justify-between text-[12px] text-white/50">
        <span>{p.employees} employees</span>
        <span className="text-emerald-400 font-mono">{p.growth}</span>
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {p.tech.map((t) => (
          <span key={t} className="text-[11px] rounded-full border border-white/[0.06] bg-white/[0.02] px-2 py-0.5 text-white/60">
            {t}
          </span>
        ))}
      </div>
      <div className="mt-4 flex items-center gap-2">
        <Button
          data-testid={`li-connect-${p.id}`}
          size="sm"
          className={`rounded-full ${p.connected ? "bg-white/[0.05] text-white/60" : "bg-indigo-500 hover:bg-indigo-400"}`}
          disabled={p.connected}
          onClick={() => toast.success(`Connection request sent to ${p.name}`)}
        >
          {p.connected ? "Connected" : <><UserPlus className="h-3.5 w-3.5 mr-1.5" /> Connect</>}
        </Button>
        <Button size="sm" variant="ghost" className="rounded-full text-white/70 border border-white/[0.06]">
          <Sparkles className="h-3.5 w-3.5 mr-1.5" /> AI message
        </Button>
      </div>
    </motion.div>
  );
}

function InstagramCard({ p, i }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: i * 0.04, duration: 0.3 }}
      className="glass-card p-5"
      data-testid={`ig-card-${p.id}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-full bg-gradient-to-tr from-pink-500 via-red-500 to-amber-400 p-[2px]">
            <div className="h-full w-full rounded-full bg-[#0f0f13] grid place-items-center">
              <Instagram className="h-4 w-4" />
            </div>
          </div>
          <div>
            <div className="font-display text-[15px] text-white">{p.handle}</div>
            <div className="text-[12px] text-white/50">{p.city}</div>
          </div>
        </div>
        <div className="text-right">
          <div className="font-mono text-lg">{p.branding}</div>
          <div className="font-mono text-[10px] uppercase tracking-wider text-white/40">brand</div>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3">
        <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-2.5">
          <div className="font-mono text-[10px] uppercase tracking-wider text-white/50">Followers</div>
          <div className="mt-0.5 font-mono">{p.followers}</div>
        </div>
        <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-2.5">
          <div className="font-mono text-[10px] uppercase tracking-wider text-white/50">Engagement</div>
          <div className="mt-0.5 font-mono">{p.engagement}</div>
        </div>
      </div>
      <div className="mt-4 space-y-1.5">
        <div className="flex items-center gap-2 text-[12.5px] text-white/70"><Globe className="h-3.5 w-3.5 text-white/40" /> {p.website}</div>
        <div className="flex items-center gap-2 text-[12.5px] text-white/70"><Mail  className="h-3.5 w-3.5 text-white/40" /> {p.email}</div>
      </div>
      <div className="mt-4 flex items-center gap-2">
        <Button
          data-testid={`ig-dm-${p.id}`}
          size="sm"
          className="rounded-full bg-pink-500 hover:bg-pink-400"
          onClick={() => toast.success(`DM queued to ${p.handle}`)}
        >
          <Send className="h-3.5 w-3.5 mr-1.5" /> DM
        </Button>
        <Button size="sm" variant="ghost" className="rounded-full text-white/70 border border-white/[0.06]">
          <Sparkles className="h-3.5 w-3.5 mr-1.5" /> Analyze
        </Button>
      </div>
    </motion.div>
  );
}
